import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from "../Navbar";  // Added Navbar import

// Get API URL from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL ;
const API_BASE = `${API_BASE_URL}/api`;

// Helper function to map user roles to approval levels
const mapRoleToApprovalType = (role) => {
  const mapping = {
    'ULB': 'ulb',
    'DISTRICT_ADMIN': 'district',
    'DIVISIONAL_OFFICER': 'division',
    'DMA_DESK_OFFICER': 'dma',
    'DMA_DESK_USER': 'dma',
    'SUPER_ADMIN': 'dma'
  };
  return mapping[role] || 'dma';
};

// Helper function to format role for display
const formatRoleDisplay = (role) => {
  const displayMap = {
    'ULB': 'ULB',
    'DISTRICT_ADMIN': 'District',
    'DIVISIONAL_OFFICER': 'Division',
    'DMA_DESK_OFFICER': 'DMA',
    'DMA_DESK_USER': 'DMA',
    'SUPER_ADMIN': 'DMA'
  };
  return displayMap[role] || role || 'Unknown';
};

const parseUlbData = (data) => {
  let ulbs = [];

  if (data && typeof data === 'object' && data.success === true && Array.isArray(data.ulbs)) {
    console.log('📊 Found ulbs array with', data.ulbs.length, 'items');
    ulbs = data.ulbs.map(item => ({
      id: String(item.id),
      ulb_name: item.ulb_name || "Unknown ULB",
      district_id: item.district_id,
      division_id: item.division_id
    }));
  } else if (Array.isArray(data)) {
    console.log('📊 Direct array response with', data.length, 'items');
    ulbs = data.map(item => ({
      id: String(item.id),
      ulb_name: item.ulb_name || "Unknown ULB",
      district_id: item.district_id,
      division_id: item.division_id
    }));
  } else if (data && typeof data === 'object') {
    if (data.success === true && Array.isArray(data.data)) {
      console.log('📊 Success wrapper with data array,', data.data.length, 'items');
      ulbs = data.data.map(item => ({
        id: String(item.id),
        ulb_name: item.ulb_name || "Unknown ULB",
        district_id: item.district_id,
        division_id: item.division_id
      }));
    } else if (data.data && Array.isArray(data.data)) {
      console.log('📊 Data wrapper with', data.data.length, 'items');
      ulbs = data.data.map(item => ({
        id: String(item.id),
        ulb_name: item.ulb_name || "Unknown ULB",
        district_id: item.district_id,
        division_id: item.division_id
      }));
    }
  }

  console.log(`✅ Parsed ${ulbs.length} ULBs`);
  return ulbs;
};

// ==================== TRANSFER REQUEST FORM COMPONENT ====================
export const TransferRequestForm = ({ user, userType, onSuccess, onCancel }) => {
  const safeUser = user || {
    id: '',
    designation: '',
    district_id: '',
    district_name: '',
    division_id: '',
    division_name: '',
    ulb_id: '',
    ulb_name: '',
    full_name: '',
    email: '',
    username: ''
  };

  console.log('Current user data:', safeUser);

  const [formData, setFormData] = useState({
    transferType: 'request',
    fromDistrict: safeUser.district_id || '',
    fromDivision: safeUser.division_id || '',
    fromUlb: safeUser.ulb_id || '',
    fromDesignation: safeUser.designation || '',
    toDistrict: '',
    toDivision: '',
    toUlb: '',
    toDesignation: '',
    toRole: '',
    reason: '',
    letter: null
  });

  const [divisions, setDivisions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [ulbs, setUlbs] = useState([]);
  const [toDistricts, setToDistricts] = useState([]);
  const [toUlbs, setToUlbs] = useState([]);
  
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingUlbs, setLoadingUlbs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [approvalInfo, setApprovalInfo] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');

  const availableRoles = [
    { value: 'ulb', label: 'ULB' },
    { value: 'district_admin', label: 'District Admin' },
    { value: 'divisional_officer', label: 'Divisional Officer' }
  ];

  useEffect(() => {
    fetchDivisions();
    fetchAllDistricts();
    fetchUlbsForCurrentUser();
  }, []);

  useEffect(() => {
    if (formData.toDivision && formData.toDivision !== '0') {
      fetchDistrictsForDivision(formData.toDivision);
    } else {
      setToDistricts([]);
    }
  }, [formData.toDivision]);

  useEffect(() => {
    if (formData.toDistrict && formData.toDistrict !== '0') {
      fetchUlbsForDistrict(formData.toDistrict);
    } else {
      setToUlbs([]);
    }
  }, [formData.toDistrict]);

  const fetchDivisions = async () => {
    try {
      setLoadingDivisions(true);
      const response = await axios.get(`${API_BASE}/meta/divisions`, {
        params: { state_id: 1 },
        timeout: 10000
      });
      
      let formattedDivisions = [];
      const responseData = response.data?.data || response.data;
      
      if (Array.isArray(responseData)) {
        formattedDivisions = responseData.map(item => ({
          id: String(item.id || item.division_id),
          name: item.name || item.division_name,
          name_marathi: item.name_mr || item.name_marathi || ''
        }));
      }
      setDivisions(formattedDivisions);
    } catch (error) {
      console.error('❌ Error fetching divisions:', error);
    } finally {
      setLoadingDivisions(false);
    }
  };

  const fetchAllDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/meta/districts`, {
        timeout: 10000
      });
      
      let formattedDistricts = [];
      const responseData = response.data?.data || response.data;
      
      if (Array.isArray(responseData)) {
        formattedDistricts = responseData.map(item => ({
          id: String(item.id),
          name: item.name,
          name_marathi: item.name_marathi || '',
          division_id: item.division_id
        }));
      }
      setDistricts(formattedDistricts);
    } catch (error) {
      console.error('❌ Error fetching all districts:', error);
    }
  };

  const fetchUlbsForCurrentUser = async () => {
    if (safeUser.district_id) {
      try {
        const response = await axios.get(`${API_BASE}/meta/ulbs`, {
          params: { district_id: safeUser.district_id },
          timeout: 10000
        });
        const formattedUlbs = parseUlbData(response.data);
        setUlbs(formattedUlbs);
      } catch (error) {
        console.error('❌ Error fetching ULBs:', error);
      }
    }
  };

  const fetchDistrictsForDivision = async (divisionId) => {
    if (!divisionId || divisionId === '0') {
      setToDistricts([]);
      return;
    }

    try {
      setLoadingDistricts(true);
      const response = await axios.get(`${API_BASE}/meta/districts`, {
        params: { division_id: divisionId },
        timeout: 10000
      });
      
      let formattedDistricts = [];
      const responseData = response.data?.data || response.data;
      
      if (Array.isArray(responseData)) {
        formattedDistricts = responseData.map(item => ({
          id: String(item.id || item.district_id),
          name: item.name || item.district_name,
          name_marathi: item.name_mr || item.name_marathi || '',
          division_id: item.division_id
        }));
      }
      setToDistricts(formattedDistricts);
    } catch (error) {
      console.error('❌ Error fetching districts:', error);
      setToDistricts([]);
    } finally {
      setLoadingDistricts(false);
    }
  };

  const fetchUlbsForDistrict = async (districtId) => {
    if (!districtId || districtId === '0') {
      setToUlbs([]);
      return;
    }

    try {
      setLoadingUlbs(true);
      const response = await axios.get(`${API_BASE}/meta/ulbs`, {
        params: { district_id: districtId },
        timeout: 15000
      });
      
      const formattedUlbs = parseUlbData(response.data);
      setToUlbs(formattedUlbs);
    } catch (error) {
      console.error('❌ Error fetching ULBs:', error);
      setToUlbs([]);
    } finally {
      setLoadingUlbs(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === 'toDivision') {
      setFormData(prev => ({
        ...prev,
        toDistrict: '',
        toUlb: '',
        toRole: ''
      }));
    }
    if (name === 'toDistrict') {
      setFormData(prev => ({
        ...prev,
        toUlb: '',
        toRole: ''
      }));
    }
    if (name === 'transferType') {
      setFormData(prev => ({
        ...prev,
        toRole: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      console.log('📎 File selected:', { 
        name: file.name, 
        type: file.type, 
        size: file.size 
      });
      setFormData(prev => ({
        ...prev,
        letter: file
      }));
      setSelectedFileName(file.name);
    } else {
      setFormData(prev => ({
        ...prev,
        letter: null
      }));
      setSelectedFileName('');
    }
  };

  // ==================== FIXED handleSubmit FUNCTION ====================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    setDebugInfo(null);

    try {
      // Validate required fields
      const isEmpty = (v) => v === undefined || v === null || v === '';
      
      const missingFields = [];
      if (isEmpty(formData.fromDistrict)) missingFields.push('fromDistrict');
      if (isEmpty(formData.fromDivision)) missingFields.push('fromDivision');
      if (isEmpty(formData.toDistrict)) missingFields.push('toDistrict');
      if (isEmpty(formData.toDivision)) missingFields.push('toDivision');
      if (isEmpty(formData.reason)) missingFields.push('reason');
      
      if (formData.transferType === 'promotional' && isEmpty(formData.toRole)) {
        missingFields.push('toRole');
      }
      
      if (missingFields.length > 0) {
        setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      
      const userId = user?.id ? parseInt(user.id) : 1;
      const username = user?.username || user?.full_name || `user_${userId}`;
      
      // Append all fields to FormData
      formDataToSend.append('user_id', String(userId));
      formDataToSend.append('username', username);
      formDataToSend.append('user_type', userType);
      formDataToSend.append('transfer_type', formData.transferType);
      formDataToSend.append('fromDesignation', formData.fromDesignation || '');
      formDataToSend.append('fromDistrict', String(formData.fromDistrict));
      formDataToSend.append('fromDivision', String(formData.fromDivision));
      formDataToSend.append('fromUlb', formData.fromUlb ? String(formData.fromUlb) : '');
      formDataToSend.append('toDistrict', String(formData.toDistrict));
      formDataToSend.append('toDivision', String(formData.toDivision));
      formDataToSend.append('toUlb', formData.toUlb ? String(formData.toUlb) : '');
      formDataToSend.append('toDesignation', formData.toDesignation || '');
      
      if (formData.transferType === 'promotional') {
        formDataToSend.append('to_role', formData.toRole);
      }
      
      formDataToSend.append('reason', formData.reason);
      
      // CRITICAL: Append file if selected
      if (formData.letter) {
        console.log('📎 Appending file to FormData:', formData.letter.name);
        formDataToSend.append('letter', formData.letter);
      } else {
        console.log('⚠️ No file selected, sending without file');
      }

      // Log all FormData entries for debugging
      console.log('📦 FormData contents:');
      for (let pair of formDataToSend.entries()) {
        if (pair[0] === 'letter' && pair[1] instanceof File) {
          console.log(`   ${pair[0]}: ${pair[1].name} (${pair[1].size} bytes, ${pair[1].type})`);
        } else {
          console.log(`   ${pair[0]}: ${pair[1]}`);
        }
      }

      // Use fetch API for reliable multipart handling
      const response = await fetch(`${API_BASE_URL}/api/transfer/submit`, {
        method: 'POST',
        headers: {
          'x-user-id': String(userId),
          'x-user-name': username
          // Do NOT set Content-Type header - let fetch set it with boundary
        },
        body: formDataToSend
      });

      const responseData = await response.json();
      console.log('✅ Response:', responseData);

      if (responseData.success) {
        setSuccess('Transfer request submitted successfully!');
        setApprovalInfo(responseData.data);
        
        // Reset form
        setFormData({
          transferType: 'request',
          fromDistrict: safeUser.district_id || '',
          fromDivision: safeUser.division_id || '',
          fromUlb: safeUser.ulb_id || '',
          fromDesignation: safeUser.designation || '',
          toDistrict: '',
          toDivision: '',
          toUlb: '',
          toDesignation: '',
          toRole: '',
          reason: '',
          letter: null
        });
        setSelectedFileName('');

        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) fileInput.value = '';

        if (onSuccess) {
          onSuccess(responseData);
        }
      } else {
        setError(responseData.error || 'Failed to submit transfer request');
      }
    } catch (error) {
      console.error('❌ Submission error:', error);
      
      setDebugInfo({
        message: error.message,
        stack: error.stack
      });
      setError('Request failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  // ==================== END OF FIXED handleSubmit ====================

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '30px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      fontFamily: 'Arial, sans-serif'
    },
    header: {
      color: '#2c3e50',
      marginBottom: '25px',
      borderBottom: '3px solid #3498db',
      paddingBottom: '15px',
      fontSize: '24px',
      fontWeight: '600',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#7f8c8d'
    },
    errorAlert: {
      backgroundColor: '#fee',
      color: '#c0392b',
      padding: '12px 15px',
      borderRadius: '6px',
      marginBottom: '20px',
      border: '1px solid #e74c3c',
      fontSize: '14px'
    },
    successAlert: {
      backgroundColor: '#d4edda',
      color: '#155724',
      padding: '12px 15px',
      borderRadius: '6px',
      marginBottom: '20px',
      border: '1px solid #c3e6cb',
      fontSize: '14px'
    },
    debugAlert: {
      backgroundColor: '#e7f3ff',
      color: '#004085',
      padding: '12px 15px',
      borderRadius: '6px',
      marginBottom: '20px',
      border: '1px solid #b8daff',
      fontSize: '12px',
      fontFamily: 'monospace',
      whiteSpace: 'pre-wrap'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#34495e',
      fontSize: '14px'
    },
    radioGroup: {
      display: 'flex',
      gap: '25px',
      flexWrap: 'wrap',
      padding: '10px 0'
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#2c3e50',
      fontSize: '14px',
      cursor: 'pointer'
    },
    radioInput: {
      width: '16px',
      height: '16px',
      cursor: 'pointer',
      accentColor: '#3498db'
    },
    currentLocation: {
      backgroundColor: '#e8f4fd',
      padding: '15px 20px',
      borderRadius: '8px',
      marginBottom: '25px',
      border: '1px solid #bde0fe'
    },
    currentLocationTitle: {
      margin: '0 0 15px 0',
      color: '#2c3e50',
      fontSize: '16px',
      fontWeight: '600'
    },
    currentLocationGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '15px',
      marginBottom: '10px'
    },
    currentLocationItem: {
      color: '#2c3e50',
      fontSize: '14px',
      padding: '8px 12px',
      backgroundColor: '#ffffff',
      borderRadius: '6px',
      border: '1px solid #bde0fe'
    },
    currentLocationLabel: {
      fontWeight: '600',
      color: '#2c3e50',
      marginRight: '8px',
      fontSize: '13px'
    },
    currentLocationValue: {
      color: '#2c3e50',
      fontSize: '14px',
      fontWeight: '500'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #dcdde1',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#2c3e50',
      backgroundColor: '#fff',
      transition: 'border-color 0.3s',
      outline: 'none',
      marginBottom: '10px'
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #dcdde1',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#2c3e50',
      backgroundColor: '#fff',
      transition: 'border-color 0.3s',
      outline: 'none',
      marginBottom: '10px',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #dcdde1',
      borderRadius: '6px',
      fontSize: '14px',
      color: '#2c3e50',
      backgroundColor: '#fff',
      transition: 'border-color 0.3s',
      outline: 'none',
      resize: 'vertical',
      minHeight: '100px'
    },
    fileInput: {
      width: '100%',
      padding: '8px',
      border: '1px dashed #3498db',
      borderRadius: '6px',
      backgroundColor: '#f8f9fa',
      cursor: 'pointer'
    },
    button: {
      backgroundColor: loading ? '#95a5a6' : '#3498db',
      color: 'white',
      padding: '12px 25px',
      border: 'none',
      borderRadius: '6px',
      cursor: loading ? 'not-allowed' : 'pointer',
      fontSize: '16px',
      fontWeight: '600',
      width: '100%',
      transition: 'background-color 0.3s',
      marginTop: '10px'
    },
    approvalInfo: {
      marginTop: '20px',
      padding: '15px',
      backgroundColor: '#d4edda',
      color: '#155724',
      border: '1px solid #c3e6cb',
      borderRadius: '6px'
    },
    approvalTitle: {
      fontSize: '16px',
      fontWeight: '600',
      marginBottom: '8px'
    },
    approvalText: {
      fontSize: '14px',
      marginBottom: '5px'
    },
    infoBox: {
      backgroundColor: '#f0f7ff',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px',
      border: '1px solid #bde0fe'
    },
    infoTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '15px'
    },
    roleBadge: {
      backgroundColor: '#9b59b6',
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      marginLeft: '8px'
    },
    fileNameDisplay: {
      marginTop: '5px',
      fontSize: '12px',
      color: '#28a745',
      display: 'block'
    }
  };

  const currentDivisionName = divisions.find(d => String(d.id) === String(formData.fromDivision))?.name || 
                             safeUser.division_name || 
                             (formData.fromDivision ? `Division ID: ${formData.fromDivision}` : 'Not set');
                             
  const currentDistrictName = districts.find(d => String(d.id) === String(formData.fromDistrict))?.name || 
                             safeUser.district_name || 
                             (formData.fromDistrict ? `District ID: ${formData.fromDistrict}` : 'Not set');
                             
  const currentUlbName = ulbs.find(u => String(u.id) === String(formData.fromUlb))?.ulb_name || 
                        safeUser.ulb_name || 
                        (formData.fromUlb ? `ULB ID: ${formData.fromUlb}` : 'Not set');

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        Transfer Request Form
        {onCancel && (
          <button onClick={onCancel} style={styles.closeButton}>×</button>
        )}
      </div>

      {error && <div style={styles.errorAlert}><strong>Error!</strong> {error}</div>}
      {debugInfo && (
        <div style={styles.debugAlert}>
          <strong>Debug Info:</strong>
          <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
        </div>
      )}
      {success && <div style={styles.successAlert}><strong>Success!</strong> {success}</div>}

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div style={styles.formGroup}>
          <label style={styles.label}>Transfer Type:</label>
          <div style={styles.radioGroup}>
            <label style={styles.radioLabel}>
              <input type="radio" name="transferType" value="promotional" 
                checked={formData.transferType === 'promotional'} onChange={handleInputChange} />
              Promotional Transfer
              {formData.transferType === 'promotional' && <span style={styles.roleBadge}>Role Change</span>}
            </label>
            <label style={styles.radioLabel}>
              <input type="radio" name="transferType" value="request" 
                checked={formData.transferType === 'request'} onChange={handleInputChange} />
              Request Transfer
            </label>
            <label style={styles.radioLabel}>
              <input type="radio" name="transferType" value="regular" 
                checked={formData.transferType === 'regular'} onChange={handleInputChange} />
              Regular Transfer
            </label>
          </div>
        </div>

        <div style={styles.currentLocation}>
          <h4 style={styles.currentLocationTitle}>Your Current Location</h4>
          <div style={styles.currentLocationGrid}>
            <div style={styles.currentLocationItem}>
              <span style={styles.currentLocationLabel}>Username:</span>
              <span style={styles.currentLocationValue}>{safeUser.username || 'Not set'}</span>
            </div>
            <div style={styles.currentLocationItem}>
              <span style={styles.currentLocationLabel}>Current Role:</span>
              <span style={styles.currentLocationValue}>{userType || 'Not set'}</span>
            </div>
            <div style={styles.currentLocationItem}>
              <span style={styles.currentLocationLabel}>Designation:</span>
              <span style={styles.currentLocationValue}>{formData.fromDesignation || safeUser.designation || 'Not set'}</span>
            </div>
            <div style={styles.currentLocationItem}>
              <span style={styles.currentLocationLabel}>Division:</span>
              <span style={styles.currentLocationValue}>{currentDivisionName}</span>
            </div>
            <div style={styles.currentLocationItem}>
              <span style={styles.currentLocationLabel}>District:</span>
              <span style={styles.currentLocationValue}>{currentDistrictName}</span>
            </div>
            {userType === 'ULB' && (
              <div style={styles.currentLocationItem}>
                <span style={styles.currentLocationLabel}>ULB:</span>
                <span style={styles.currentLocationValue}>{currentUlbName}</span>
              </div>
            )}
          </div>
          <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '10px' }}>
            * These are your current details. They cannot be changed.
          </p>
        </div>

        <input type="hidden" name="fromDivision" value={formData.fromDivision} />
        <input type="hidden" name="fromDistrict" value={formData.fromDistrict} />
        <input type="hidden" name="fromUlb" value={formData.fromUlb} />
        <input type="hidden" name="fromDesignation" value={formData.fromDesignation} />

        <div style={styles.infoBox}>
          <h4 style={styles.infoTitle}>
            {formData.transferType === 'promotional' ? 'Promotion Details' : 'Transfer To (Destination)'}
          </h4>
          
          {formData.transferType === 'promotional' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Destination Role *</label>
              <select 
                name="toRole" 
                value={formData.toRole} 
                onChange={handleInputChange} 
                style={styles.select} 
                required
              >
                <option value="">Select New Role</option>
                {availableRoles
                  .filter(role => role.value !== userType.toLowerCase())
                  .map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
              </select>
              <small style={{ color: '#7f8c8d' }}>
                Select the new role for promotional transfer
              </small>
            </div>
          )}
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Destination Division *</label>
            <select name="toDivision" value={formData.toDivision} onChange={handleInputChange} 
              style={styles.select} required disabled={loadingDivisions}>
              <option value="">Select Division</option>
              {divisions.map(div => (
                <option key={div.id} value={div.id}>
                  {div.name} {div.name_marathi ? `- ${div.name_marathi}` : ''}
                </option>
              ))}
            </select>
            {loadingDivisions && <small>Loading divisions...</small>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Destination District *</label>
            <select name="toDistrict" value={formData.toDistrict} onChange={handleInputChange} 
              style={styles.select} required disabled={!formData.toDivision || loadingDistricts}>
              <option value="">Select District</option>
              {toDistricts.map(dist => (
                <option key={dist.id} value={dist.id}>
                  {dist.name} {dist.name_marathi ? `- ${dist.name_marathi}` : ''}
                </option>
              ))}
            </select>
            {loadingDistricts && <small>Loading districts...</small>}
          </div>

          {userType === 'ULB' && formData.transferType !== 'promotional' && (
            <div style={styles.formGroup}>
              <label style={styles.label}>Destination ULB</label>
              <select name="toUlb" value={formData.toUlb} onChange={handleInputChange} 
                style={styles.select} disabled={!formData.toDistrict || loadingUlbs}>
                <option value="">Select ULB</option>
                {toUlbs.map(ulb => (
                  <option key={ulb.id} value={ulb.id}>{ulb.ulb_name}</option>
                ))}
              </select>
              {loadingUlbs && <small>Loading ULBs...</small>}
            </div>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>New Designation (if applicable)</label>
            <input type="text" name="toDesignation" placeholder="Enter new designation" 
              value={formData.toDesignation} onChange={handleInputChange} style={styles.input} />
          </div>
          
          {formData.transferType === 'promotional' && (
            <div style={{ backgroundColor: '#f0f7ff', padding: '10px', borderRadius: '6px', marginTop: '10px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#2980b9' }}>
                <strong>Note:</strong> Upon approval, you will be moved to the new role table and removed from your current role table.
              </p>
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Reason for Transfer *</label>
          <textarea name="reason" value={formData.reason} onChange={handleInputChange} 
            style={styles.textarea} required placeholder="Please provide detailed reason..." />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Upload Supporting Letter (Optional):</label>
          <input 
            type="file" 
            onChange={handleFileChange} 
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" 
            style={styles.fileInput} 
          />
          {selectedFileName && (
            <small style={styles.fileNameDisplay}>✓ Selected: {selectedFileName}</small>
          )}
          <small>Max 5MB. Allowed: PDF, DOC, DOCX, JPG, PNG</small>
        </div>

        <button type="submit" disabled={loading} style={styles.button}>
          {loading ? 'Submitting...' : 'Submit Transfer Request'}
        </button>

        {approvalInfo && (
          <div style={styles.approvalInfo}>
            <div style={styles.approvalTitle}>Request Submitted Successfully!</div>
            <div style={styles.approvalText}><strong>Request ID:</strong> #{approvalInfo.id || approvalInfo.requestId}</div>
            <div style={styles.approvalText}><strong>Transfer Type:</strong> {approvalInfo.transfer_type || formData.transferType}</div>
            {formData.transferType === 'promotional' && formData.toRole && (
              <div style={styles.approvalText}><strong>New Role:</strong> {formData.toRole}</div>
            )}
            <div style={styles.approvalText}><strong>Current Approval Level:</strong> {approvalInfo.current_approval_level}</div>
            <div style={styles.approvalText}><strong>Next Approver:</strong> {approvalInfo.tracking_info?.next_approver}</div>
            {approvalInfo.letter_uploaded && (
              <div style={styles.approvalText}><strong>File Uploaded:</strong> ✓</div>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

// ==================== TRANSFER REQUESTS LIST COMPONENT ====================
export const TransferRequestsList = ({ userId, userType }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (userId) {
      console.log('📋 TransferRequestsList mounted with userId:', userId, 'userType:', userType);
      fetchRequests();
    } else {
      setLoading(false);
      setError('User information not available. Please log in again.');
    }
  }, [userId, userType]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const url = `${API_BASE_URL}/api/transfer/user/${userId}`;
      
      console.log('📡 Fetching requests from:', url);
      
      const response = await axios.get(url, { 
        headers: { 
          'x-user-id': String(userId),
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });
      
      console.log('✅ Response:', response.data);
      
      if (response.data.success) {
        setRequests(response.data.data || []);
        console.log(`✅ Set ${response.data.data?.length || 0} requests`);
        
        if (response.data.data && response.data.data.length > 0) {
          console.log('📝 First request:', response.data.data[0]);
        }
      } else {
        setError(response.data.error || 'Failed to fetch requests');
      }
    } catch (error) {
      console.error('❌ Error fetching requests:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        setError(error.response.data?.error || `Server error: ${error.response.status}`);
      } else if (error.request) {
        console.error('No response received:', error.request);
        setError('No response from server. Please check if backend is running.');
      } else {
        setError('Request failed: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: { backgroundColor: '#fff3cd', color: '#856404' },
      forwarded: { backgroundColor: '#cce5ff', color: '#004085' },
      approved: { backgroundColor: '#d4edda', color: '#155724' },
      rejected: { backgroundColor: '#f8d7da', color: '#721c24' }
    };
    const style = statusStyles[status] || statusStyles.pending;
    return (
      <span style={{
        ...style,
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '600',
        display: 'inline-block'
      }}>
        {status?.toUpperCase() || 'PENDING'}
      </span>
    );
  };

  const getTransferTypeBadge = (type) => {
    const typeStyles = {
      promotional: { backgroundColor: '#9b59b6', color: 'white' },
      request: { backgroundColor: '#3498db', color: 'white' },
      regular: { backgroundColor: '#2ecc71', color: 'white' }
    };
    const style = typeStyles[type] || typeStyles.request;
    return (
      <span style={{
        ...style,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '10px',
        fontWeight: '600',
        marginLeft: '8px'
      }}>
        {type?.toUpperCase()}
      </span>
    );
  };

  const getFileLink = (request) => {
    if (request.letter_relative_path) {
      const fileUrl = `${API_BASE_URL}${request.letter_relative_path}`;
      return (
        <div style={{ marginTop: '10px' }}>
          <a 
            href={fileUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#3498db', 
              textDecoration: 'none',
              fontSize: '12px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            📎 View Uploaded Letter
          </a>
          {request.letter_metadata && (
            <span style={{ fontSize: '11px', color: '#7f8c8d', marginLeft: '10px' }}>
              ({Math.round(request.letter_metadata.size / 1024)} KB)
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const filteredRequests = requests.filter(r => filter === 'all' || r.status === filter);

  const styles = {
    container: { 
      padding: '30px', 
      backgroundColor: '#f5f7fa', 
      minHeight: '100vh' 
    },
    header: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '25px', 
      backgroundColor: '#fff', 
      padding: '20px', 
      borderRadius: '10px', 
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)' 
    },
    title: { 
      color: '#2c3e50', 
      fontSize: '24px', 
      fontWeight: '600', 
      margin: 0 
    },
    filterSelect: { 
      padding: '8px 12px', 
      border: '1px solid #dcdde1', 
      borderRadius: '6px', 
      fontSize: '14px' 
    },
    loadingContainer: { 
      textAlign: 'center', 
      padding: '50px', 
      backgroundColor: '#fff', 
      borderRadius: '10px' 
    },
    errorContainer: { 
      backgroundColor: '#f8d7da', 
      color: '#721c24', 
      padding: '15px', 
      borderRadius: '8px' 
    },
    requestCard: { 
      backgroundColor: '#fff', 
      borderRadius: '10px', 
      padding: '20px', 
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)', 
      marginBottom: '20px' 
    },
    requestHeader: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: '15px', 
      paddingBottom: '15px', 
      borderBottom: '1px solid #ecf0f1' 
    },
    requestId: { 
      fontSize: '18px', 
      fontWeight: '600', 
      color: '#2c3e50', 
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    requestInfo: { 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '15px', 
      marginBottom: '15px' 
    },
    infoItem: { 
      fontSize: '14px', 
      color: '#34495e' 
    },
    infoLabel: { 
      fontWeight: '600', 
      color: '#7f8c8d', 
      marginRight: '5px' 
    },
    reasonBox: { 
      backgroundColor: '#f8f9fa', 
      padding: '12px 15px', 
      borderRadius: '6px', 
      marginBottom: '15px', 
      fontSize: '14px' 
    },
    approvalLevel: { 
      marginTop: '15px', 
      padding: '8px 12px', 
      backgroundColor: '#f0f7ff', 
      borderRadius: '6px', 
      fontSize: '13px', 
      color: '#2980b9',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <p>Loading your transfer requests...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <strong>Error!</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>My Transfer Requests</h2>
        <div>
          <label style={{ marginRight: '10px' }}>Filter:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} style={styles.filterSelect}>
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="forwarded">Forwarded</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {filteredRequests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#fff', borderRadius: '10px' }}>
          <p style={{ color: '#7f8c8d' }}>No transfer requests found</p>
        </div>
      ) : (
        filteredRequests.map(request => (
          <div key={request.id} style={styles.requestCard}>
            <div style={styles.requestHeader}>
              <h3 style={styles.requestId}>
                Request #{request.id}
                {getTransferTypeBadge(request.transfer_type)}
              </h3>
              {getStatusBadge(request.status)}
            </div>
            <div style={styles.requestInfo}>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Type:</span> {request.transfer_type}
                {request.transfer_type === 'promotional' && request.to_role && (
                  <span style={{ marginLeft: '5px', color: '#9b59b6' }}>→ {request.to_role}</span>
                )}
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>From:</span> {request.from_district_name || request.from_district}
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>To:</span> {request.to_district_name || request.to_district}
              </div>
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>Submitted:</span> {new Date(request.submitted_at).toLocaleDateString()}
              </div>
            </div>
            <div style={styles.reasonBox}>
              <strong>Reason:</strong> {request.reason}
            </div>
            <div style={styles.approvalLevel}>
              <span><strong>Current Level:</strong> {request.current_approval_level}</span>
              {request.status === 'pending' && (
                <span style={{ color: '#e67e22' }}>⏳ Awaiting approval</span>
              )}
              {request.status === 'approved' && request.transfer_type === 'promotional' && (
                <span style={{ color: '#27ae60' }}>✓ Promoted to new role</span>
              )}
            </div>
            {getFileLink(request)}
          </div>
        ))
      )}
    </div>
  );
};

// ==================== APPROVAL DASHBOARD COMPONENT ====================
export const ApprovalDashboard = ({ approverType, approverId, approverName }) => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionData, setActionData] = useState({ status: 'approved', remarks: '' });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (approverId && approverType) {
      console.log('📋 ApprovalDashboard mounted with:', { approverType, approverId, approverName });
      fetchPendingRequests();
    } else {
      setLoading(false);
      setError('Approver information not available.');
    }
  }, [approverType, approverId]);

  const fetchPendingRequests = async () => {
    try {
      setLoading(true);
      setError('');
      
      const url = `${API_BASE_URL}/api/transfer/pending/${approverType}`;
      
      console.log('📡 Fetching pending requests from:', url);
      
      const response = await axios.get(url, { 
        headers: { 'x-user-id': String(approverId) },
        timeout: 10000
      });
      
      console.log('✅ Response:', response.data);
      
      if (response.data.success) {
        setPendingRequests(response.data.data || []);
        console.log(`✅ Set ${response.data.data?.length || 0} pending requests`);
      } else {
        setError(response.data.error || 'Failed to fetch pending requests');
      }
    } catch (error) {
      console.error('❌ Error fetching pending requests:', error);
      if (error.response) {
        setError(error.response.data?.error || `Server error: ${error.response.status}`);
      } else if (error.request) {
        setError('No response from server. Please check if backend is running.');
      } else {
        setError('Request failed: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (requestId) => {
    setProcessing(true);
    try {
      const url = `${API_BASE_URL}/api/transfer/approve/${requestId}`;
      
      console.log('📡 Submitting approval to:', url);
      console.log('📦 Approval data:', { 
        status: actionData.status, 
        remarks: actionData.remarks, 
        approverId, 
        approverType 
      });

      const response = await axios.post(url, {
        status: actionData.status,
        remarks: actionData.remarks,
        approverId: parseInt(approverId),
        approverType: approverType,
        approverUsername: approverName
      }, { 
        headers: { 'x-user-id': String(approverId) },
        timeout: 10000
      });

      console.log('✅ Approval response:', response.data);

      if (response.data.success) {
        alert(`Request ${actionData.status} successfully`);
        setSelectedRequest(null);
        setActionData({ status: 'approved', remarks: '' });
        fetchPendingRequests();
      } else {
        alert('Failed: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error processing approval:', error);
      if (error.response) {
        alert(error.response.data?.error || `Server error: ${error.response.status}`);
      } else if (error.request) {
        alert('No response from server. Please check if backend is running.');
      } else {
        alert('Request failed: ' + error.message);
      }
    } finally {
      setProcessing(false);
    }
  };

  const getApprovalLevelInfo = (request) => {
    let baseInfo = '';
    if (request.user_type === 'ULB' || request.user_type === 'ulb') {
      if (String(request.to_district) === String(request.from_district)) 
        baseInfo = 'District Officer (DISTRICT_ADMIN) Approval Required';
      else if (String(request.to_division) === String(request.from_division)) 
        baseInfo = 'Divisional Officer (DIVISIONAL_OFFICER) Approval Required';
      else baseInfo = 'DMA (DMA_DESK_OFFICER) Approval Required';
    } else if (request.user_type === 'DISTRICT_ADMIN' || request.user_type === 'district_admin') {
      baseInfo = String(request.to_division) === String(request.from_division) 
        ? 'Divisional Officer (DIVISIONAL_OFFICER) Approval Required'
        : 'DMA (DMA_DESK_OFFICER) Approval Required';
    } else {
      baseInfo = 'DMA (DMA_DESK_OFFICER) Approval Required';
    }
    
    if (request.transfer_type === 'promotional' && request.to_role) {
      baseInfo = `[PROMOTIONAL] ${baseInfo} - Moving to ${request.to_role} role`;
    }
    
    return baseInfo;
  };

  const getTransferTypeBadge = (type) => {
    const typeStyles = {
      promotional: { backgroundColor: '#9b59b6', color: 'white' },
      request: { backgroundColor: '#3498db', color: 'white' },
      regular: { backgroundColor: '#2ecc71', color: 'white' }
    };
    const style = typeStyles[type] || typeStyles.request;
    return (
      <span style={{
        ...style,
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '10px',
        fontWeight: '600',
        marginLeft: '8px'
      }}>
        {type?.toUpperCase()}
      </span>
    );
  };

  const styles = {
    container: { padding: '30px', backgroundColor: '#f5f7fa', minHeight: '100vh' },
    header: { backgroundColor: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '25px' },
    title: { color: '#2c3e50', fontSize: '24px', fontWeight: '600', margin: '0 0 10px 0' },
    subtitle: { color: '#7f8c8d', fontSize: '14px' },
    stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginTop: '20px' },
    statCard: { backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', textAlign: 'center' },
    statNumber: { fontSize: '28px', fontWeight: '700', color: '#3498db', marginBottom: '5px' },
    statLabel: { fontSize: '13px', color: '#7f8c8d' },
    requestCard: { backgroundColor: '#fff', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '20px', cursor: 'pointer' },
    requestHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #ecf0f1' },
    requestId: { 
      fontSize: '18px', 
      fontWeight: '600', 
      color: '#2c3e50', 
      margin: 0,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    userBadge: { backgroundColor: '#3498db', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    approvalInfo: { marginTop: '15px', padding: '12px', backgroundColor: '#f0f7ff', borderRadius: '6px', fontSize: '14px', color: '#2980b9' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: '#fff', padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '90%', position: 'relative' },
    modalClose: { position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' },
    modalTitle: { fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid #3498db' },
    actionButtons: { display: 'flex', gap: '20px', marginBottom: '15px' },
    actionButton: { padding: '10px 20px', border: '2px solid', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', flex: 1 },
    approveButton: { backgroundColor: actionData.status === 'approved' ? '#d4edda' : 'transparent', borderColor: '#28a745', color: '#155724' },
    rejectButton: { backgroundColor: actionData.status === 'rejected' ? '#f8d7da' : 'transparent', borderColor: '#dc3545', color: '#721c24' },
    textarea: { width: '100%', padding: '10px', border: '1px solid #dcdde1', borderRadius: '6px', fontSize: '14px', minHeight: '80px', marginBottom: '20px' },
    submitButton: { backgroundColor: processing ? '#95a5a6' : '#3498db', color: 'white', padding: '12px 25px', border: 'none', borderRadius: '6px', cursor: processing ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: '600', width: '100%' },
    requestDetails: { backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px', marginBottom: '20px' }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '50px', backgroundColor: '#fff', borderRadius: '10px' }}>
          <p>Loading pending approvals...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={styles.container}>
        <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '8px' }}>
          <strong>Error!</strong> {error}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Approval Dashboard</h2>
        <p style={styles.subtitle}>{approverName || 'Approver'} - {formatRoleDisplay(approverType)} Level</p>
        <div style={styles.stats}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{pendingRequests.length}</div>
            <div style={styles.statLabel}>Pending</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{pendingRequests.filter(r => r.current_approval_level === mapRoleToApprovalType(approverType)).length}</div>
            <div style={styles.statLabel}>At Your Level</div>
          </div>
        </div>
      </div>

      {pendingRequests.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#fff', borderRadius: '10px' }}>
          <p style={{ color: '#7f8c8d' }}>No pending approvals at this time</p>
        </div>
      ) : (
        pendingRequests.map(request => (
          <div key={request.id} style={styles.requestCard} onClick={() => setSelectedRequest(request)}>
            <div style={styles.requestHeader}>
              <h3 style={styles.requestId}>
                Request #{request.id}
                {getTransferTypeBadge(request.transfer_type)}
              </h3>
              <span style={styles.userBadge}>{request.user_type}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '15px' }}>
              <div>
                <span style={{ fontWeight: '600', color: '#7f8c8d' }}>Applicant:</span> {request.full_name}
              </div>
              <div>
                <span style={{ fontWeight: '600', color: '#7f8c8d' }}>From:</span> {request.from_district_name || request.from_district}
              </div>
              <div>
                <span style={{ fontWeight: '600', color: '#7f8c8d' }}>To:</span> {request.to_district_name || request.to_district}
              </div>
              {request.transfer_type === 'promotional' && request.to_role && (
                <div>
                  <span style={{ fontWeight: '600', color: '#7f8c8d' }}>New Role:</span> {request.to_role}
                </div>
              )}
            </div>
            <div style={styles.approvalInfo}>{getApprovalLevelInfo(request)}</div>
          </div>
        ))
      )}

      {selectedRequest && (
        <div style={styles.modal} onClick={() => setSelectedRequest(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={() => setSelectedRequest(null)}>×</button>
            <h3 style={styles.modalTitle}>Process Request #{selectedRequest.id}</h3>
            <div style={styles.requestDetails}>
              <p><strong>Applicant:</strong> {selectedRequest.full_name}</p>
              <p><strong>Current Role:</strong> {selectedRequest.user_type}</p>
              <p><strong>Transfer Type:</strong> {selectedRequest.transfer_type}</p>
              {selectedRequest.transfer_type === 'promotional' && selectedRequest.to_role && (
                <p><strong>New Role:</strong> {selectedRequest.to_role}</p>
              )}
              <p><strong>From:</strong> {selectedRequest.from_district_name || selectedRequest.from_district}</p>
              <p><strong>To:</strong> {selectedRequest.to_district_name || selectedRequest.to_district}</p>
              <p><strong>Reason:</strong> {selectedRequest.reason}</p>
              {selectedRequest.transfer_type === 'promotional' && (
                <p style={{ color: '#9b59b6', fontWeight: 'bold' }}>
                  ⚡ This is a promotional transfer. User will be moved to new role table upon final approval.
                </p>
              )}
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>Action:</label>
              <div style={styles.actionButtons}>
                <button 
                  style={{...styles.actionButton, ...styles.approveButton}} 
                  onClick={() => setActionData({...actionData, status: 'approved'})}
                >
                  Approve
                </button>
                <button 
                  style={{...styles.actionButton, ...styles.rejectButton}} 
                  onClick={() => setActionData({...actionData, status: 'rejected'})}
                >
                  Reject
                </button>
              </div>
            </div>
            <textarea 
              style={styles.textarea} 
              value={actionData.remarks} 
              onChange={(e) => setActionData({...actionData, remarks: e.target.value})}
              placeholder="Add remarks (optional)..."
            />
            <button 
              style={styles.submitButton} 
              onClick={() => handleAction(selectedRequest.id)} 
              disabled={processing}
            >
              {processing ? 'Processing...' : `Submit ${actionData.status}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN TRANSFER MANAGEMENT COMPONENT ====================
export const TransferManagement = ({ user }) => {
  const [activeTab, setActiveTab] = useState('New Transfer');
  const [localUser, setLocalUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [language, setLanguage] = useState(() => {
    const saved = sessionStorage.getItem("dma_portal_language");
    return saved && (saved === "EN" || saved === "MR") ? saved : "EN";
  });

  const tabs = {
    'ULB': ['New Transfer', 'My Requests'],
    'DISTRICT_ADMIN': ['New Transfer', 'My Requests', 'Pending Approvals'],
    'DIVISIONAL_OFFICER': ['New Transfer', 'My Requests', 'Pending Approvals'],
    'DMA_DESK_OFFICER': ['New Transfer', 'My Requests', 'Pending Approvals', 'All Requests'],
    'DMA_DESK_USER': ['New Transfer', 'My Requests', 'Pending Approvals', 'All Requests'],
    'SUPER_ADMIN': ['New Transfer', 'My Requests', 'Pending Approvals', 'All Requests']
  };

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Language change listener
  useEffect(() => {
    const handleLanguageChange = () => {
      const savedLanguage = sessionStorage.getItem("dma_portal_language");
      if (savedLanguage && (savedLanguage === "EN" || savedLanguage === "MR")) {
        setLanguage(savedLanguage);
      }
    };
    window.addEventListener('storage', handleLanguageChange);
    return () => window.removeEventListener('storage', handleLanguageChange);
  }, []);

  const mapToStandardRole = (role) => {
    if (!role) return 'ULB';
    const roleUpper = role.toUpperCase();
    if (roleUpper === 'DMA_DESK_OFFICER') return 'DMA_DESK_OFFICER';
    if (roleUpper === 'DISTRICT_ADMIN') return 'DISTRICT_ADMIN';
    if (roleUpper === 'DIVISIONAL_OFFICER') return 'DIVISIONAL_OFFICER';
    if (roleUpper === 'SUPER_ADMIN') return 'SUPER_ADMIN';
    if (roleUpper === 'ULB') return 'ULB';
    if (roleUpper === 'DMA' || roleUpper === 'DM') return 'DMA_DESK_OFFICER';
    if (roleUpper === 'ADMIN') return 'SUPER_ADMIN';
    if (roleUpper === 'DISTRICT') return 'DISTRICT_ADMIN';
    if (roleUpper === 'DIVISION') return 'DIVISIONAL_OFFICER';
    return 'ULB';
  };

  useEffect(() => {
    const initializeUser = async () => {
      console.log('🔄 Initializing user in TransferManagement:', user);
      
      if (user?.id) {
        const possibleRole = user.role_name || user.role || user.user_type || user.workflow_role || 'ULB';
        const standardRole = mapToStandardRole(possibleRole);
        const userData = {
          ...user,
          id: user.id,
          user_type: standardRole,
          full_name: user.full_name || user.name || `User ${user.id}`,
          email: user.email || '',
          username: user.username || '',
          district_id: user.district_id || '',
          division_id: user.division_id || '',
          ulb_id: user.ulb_id || '',
          designation: user.designation || ''
        };
        console.log('✅ User initialized:', userData);
        setLocalUser(userData);
        setLoading(false);
      } else {
        try {
          const storedUser = sessionStorage.getItem('user');
          console.log('📦 Checking sessionStorage for user:', storedUser);
          
          if (storedUser) {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser?.id) {
              const possibleRole = parsedUser.role_name || parsedUser.role || parsedUser.user_type || 'ULB';
              const standardRole = mapToStandardRole(possibleRole);
              const userData = {
                ...parsedUser,
                user_type: standardRole,
                district_id: parsedUser.district_id || '',
                division_id: parsedUser.division_id || '',
                ulb_id: parsedUser.ulb_id || '',
                designation: parsedUser.designation || ''
              };
              console.log('✅ User loaded from sessionStorage:', userData);
              setLocalUser(userData);
            }
          }
        } catch (error) {
          console.error('❌ Error initializing user from sessionStorage:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    initializeUser();
  }, [user]);

  const userRole = localUser?.user_type || 'ULB';
  const userId = localUser?.id;
  const availableTabs = tabs[userRole] || tabs['ULB'];

  const getApproverType = (role) => {
    const mapping = {
      'DISTRICT_ADMIN': 'district',
      'DIVISIONAL_OFFICER': 'division',
      'DMA_DESK_OFFICER': 'dma',
      'DMA_DESK_USER': 'dma',
      'SUPER_ADMIN': 'dma'
    };
    return mapping[role] || 'dma';
  };

  const handleNavbarMenuClick = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const styles = {
    mainWrapper: {
      marginLeft: '70px',
      paddingTop: '95px',
      minHeight: '100vh',
      background: '#f5f7fa',
      transition: 'margin-left 0.25s ease',
      width: 'calc(100% - 70px)'
    },
    container: { 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '20px' 
    },
    tabContainer: { 
      display: 'flex', 
      gap: '10px', 
      marginBottom: '20px', 
      borderBottom: '2px solid #e0e0e0', 
      paddingBottom: '10px',
      flexWrap: 'wrap'
    },
    tab: { 
      padding: '10px 20px', 
      border: 'none', 
      background: 'none', 
      cursor: 'pointer', 
      fontSize: '16px', 
      fontWeight: '500', 
      color: '#666', 
      position: 'relative' 
    },
    activeTab: { 
      color: '#3498db', 
      fontWeight: '600' 
    },
    activeTabIndicator: { 
      position: 'absolute', 
      bottom: '-11px', 
      left: 0, 
      right: 0, 
      height: '3px', 
      backgroundColor: '#3498db' 
    },
    loadingContainer: { 
      textAlign: 'center', 
      padding: '50px', 
      backgroundColor: '#fff', 
      borderRadius: '10px' 
    },
    errorContainer: { 
      backgroundColor: '#f8d7da', 
      color: '#721c24', 
      padding: '15px', 
      borderRadius: '8px', 
      textAlign: 'center' 
    },
    sidebarOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 999,
      transition: 'all 0.3s ease'
    }
  };

  const renderContent = () => {
    if (!userId) {
      return (
        <div style={styles.errorContainer}>
          User information not available. Please log in again.
        </div>
      );
    }
    
    switch(activeTab) {
      case 'New Transfer':
        return <TransferRequestForm user={localUser} userType={userRole} />;
      case 'My Requests':
        return <TransferRequestsList userId={userId} userType={userRole} />;
      case 'Pending Approvals':
        return (
          <ApprovalDashboard 
            approverType={getApproverType(userRole)} 
            approverId={userId} 
            approverName={localUser?.full_name} 
          />
        );
      case 'All Requests':
        return (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            All Requests View Coming Soon
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <>
        <Navbar 
          onMenuClick={handleNavbarMenuClick}
          language={language === "EN" ? "english" : "marathi"}
          onLanguageChange={(lang) => {
            const newLang = lang === "marathi" ? "MR" : "EN";
            setLanguage(newLang);
            sessionStorage.setItem("dma_portal_language", newLang);
          }}
        />
        <div style={styles.mainWrapper}>
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <p>Loading user information...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar 
        onMenuClick={handleNavbarMenuClick}
        language={language === "EN" ? "english" : "marathi"}
        onLanguageChange={(lang) => {
          const newLang = lang === "marathi" ? "MR" : "EN";
          setLanguage(newLang);
          sessionStorage.setItem("dma_portal_language", newLang);
        }}
      />

      {/* Sidebar Overlay Backdrop */}
      {!sidebarCollapsed && (
        <div style={styles.sidebarOverlay} onClick={() => setSidebarCollapsed(true)} />
      )}

      <div style={styles.mainWrapper}>
        <div style={styles.container}>
          <div style={styles.tabContainer}>
            {availableTabs.map(tab => (
              <button 
                key={tab} 
                style={{
                  ...styles.tab,
                  ...(activeTab === tab ? styles.activeTab : {})
                }} 
                onClick={() => setActiveTab(tab)}
              >
                {tab}
                {activeTab === tab && <span style={styles.activeTabIndicator} />}
              </button>
            ))}
          </div>
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default TransferManagement;